import { useRef, useState } from 'react';

import { useT } from '../../i18n/index.js';
import { useHardwarePhotos } from './use-hardware-photos.js';

/**
 * Galerie de photos d'un build (ADR-0032), dans la fiche matériel. Upload depuis le navigateur
 * (dépôt pré-signé), vignettes et retrait. Réservée à l'édition (fiche d'un matériel existant).
 */
export function HardwarePhotoGallery({ assetId }: { assetId: string }) {
  const t = useT();
  const { photos, upload, remove } = useHardwarePhotos(assetId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await upload(file);
      }
    } catch (err) {
      // Les messages métier sont portés par une clé i18n ; sinon message générique.
      const key = err instanceof Error ? err.message : 'common.error';
      setError(
        key.startsWith('hw.photos.') ? t(key as Parameters<typeof t>[0]) : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ddp-hwphotos">
      <div className="ddp-hwphotos__head">
        <span className="ddp-hwphotos__title">
          {t('hw.photos.title')}
          {photos.length > 0 && <span className="ddp-hwphotos__count"> · {photos.length}</span>}
        </span>
        <button
          type="button"
          className="ddp-hwphotos__add"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? t('hw.photos.uploading') : t('hw.photos.add')}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(e) => {
            void onFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <p className="ddp-hwphotos__hint">{t('hw.photos.hint')}</p>

      {error && <p className="ddp-hwphotos__error">{error}</p>}

      {photos.length > 0 && (
        <ul className="ddp-hwphotos__grid">
          {photos.map((photo) => (
            <li key={photo.id} className="ddp-hwphotos__item">
              <img src={photo.url} alt={photo.caption ?? ''} loading="lazy" />
              <button
                type="button"
                className="ddp-hwphotos__del"
                disabled={busy}
                aria-label={t('hw.photos.remove')}
                title={t('hw.photos.remove')}
                onClick={() => void remove(photo.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
